// frontend/src/components/UI/FlowBackground.tsx
import { useEffect, useRef } from 'react';
import { useTheme } from '../../hooks/useTheme';

const LINES = 12;
const SPEED = 0.006;

export default function FlowBackground() {
  const ref = useRef<HTMLCanvasElement>(null);
  const { theme } = useTheme();

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0, h = 0, t = 0, raf = 0;

    const resize = () => {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const isDark = theme === 'dark';

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < LINES; i++) {
        const baseY = (h / (LINES + 1)) * (i + 1);
        const amp = 26 + (i % 3) * 14;
        const phase = i * 0.7 + t;
        const opacity = isDark
          ? (0.12 - i * 0.005)
          : (0.16 - i * 0.006);
        const g = ctx.createLinearGradient(0, 0, w, 0);
        if (isDark) {
          g.addColorStop(0, 'rgba(89,155,255,0)');
          g.addColorStop(0.5, `rgba(89,155,255,${Math.max(opacity, 0.02)})`);
          g.addColorStop(1, 'rgba(89,155,255,0)');
        } else {
          g.addColorStop(0, 'rgba(65,178,228,0)');
          g.addColorStop(0.5, `rgba(47,107,255,${Math.max(opacity, 0.02)})`);
          g.addColorStop(1, 'rgba(65,178,228,0)');
        }
        ctx.beginPath();
        for (let x = 0; x <= w; x += 8) {
          const y =
            baseY +
            Math.sin(x * 0.006 + phase) * amp +
            Math.sin(x * 0.013 + phase * 1.4) * amp * 0.4;
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.strokeStyle = g;
        ctx.lineWidth = 1.6;
        ctx.stroke();
      }
      t += SPEED;
      raf = requestAnimationFrame(draw);
    };
    draw();

    const onVisibility = () => {
      if (document.hidden) cancelAnimationFrame(raf);
      else draw();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [theme]);

  return (
    <canvas
      ref={ref}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        background: theme === 'dark' ? '#121212' : '#fdfdfd',
        pointerEvents: 'none',
      }}
    />
  );
}
